import './StaticIndicator.style.scss';

import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { AlertCircle, AuctionIcon, ClockIcon, GroupIcon, SettlementIcon, ToDoListIcon } from '../icon/Icon.component';
import { UserContext, UserContextProps } from '../../App';
import { useErrorHandler } from '../../utils/notification-utils';
import CustomTooltip from '../../components/CustomTooltip/CustomTooltip.component';
import { Link } from 'react-router-dom';
import { getFlowableCasesForOrganisationCaseTable } from '../tables/case-table-utils';
import CountUp from '../CountUp/CountUp';
import { CaseTableData } from '../../utils/case-utils';


interface StaticIndicatorValues {
  openCases: number;
  openCasesPercentage: string;
  closedCases: number;
  closedCasesPercentage: string;
  casesWithActiveSuspensions: number;
  casesWithActiveSuspensionsPercentage: string;
  openMisconductCases: number;
  openMisconductCasesPercentage: string;
  openPoorPerformanceCases: number;
  openPoorPerformanceCasesPercentage: string;
  casesWithActiveWarnings: number;
  casesWithActiveWarningsPercentage: string;
  casesWithHearingsConducted: number;
  casesWithHearingsConductedPercentage: string;
  casesWithIncapacityHearingsConducted: number;
  casesWithIncapacityHearingsConductedPercentage: string;
  casesWithDiscussionsConducted: number;
  casesWithDiscussionsConductedPercentage: string;
  casesReferredToDisputePhase: number;
  casesReferredToDisputePhasePercentage: string;
  casesWithPrivateSettlement: number;
  casesWithPrivateSettlementPercentage: string;
  casesSettledAtConciliation: number;
  casesSettledAtConciliationPercentage: string;
  casesLostAtArbitrationOrAdjudication: number;
  casesLostAtArbitrationOrAdjudicationPercentage: string;
  caseWithActiveInvestigations: number;
  caseWithActiveInvestigationsPercentage: string;
  successfulPIPCount: number;
  successfulPIPCountPercentage: string;
  unsuccessfulPIPCount: number;
  unsuccessfulPIPCountPercentage: string;
  casesWithDemotions: number;
  casesWithDemotionsPercentage: string;
  casesWithDismissals: number;
  casesWithDismissalsPercentage: string;
  casesWithCounsellingProcesses: number;
  casesWithCounsellingProcessesPercentage: string;
  casesWithActivePIPs: number;
  casesWithActivePIPsPercentage: string;
}

interface StaticIndicatorItemProps {
  title: string;
  value: number | string;
  percentage: string;
  toolTip: string;
  link?: string;
}

interface StaticIndicatorTabProps {
  color1: string;
  color2: string;
  icon?: ReactNode;
  data: StaticIndicatorItemProps[];
  total?: number | string;
}

interface StaticIndicatorProps {
  showMain?: boolean;
  showBottomMain?: boolean;
  showSub?: boolean;
  setCaseData?: (d: any[]) => void;
}

interface ExtendedCaseData extends CaseTableData {
  isWarningActive?: boolean;
  isSuspensionActive?: boolean;
  isUnderInvestigation?: boolean;
  isDisputeReferred?: boolean;
  settlementType?: string;
  pipOutcome?: string;
  finalOutcome?: string;
  caseStatus: string;
  caseNumber: string;
  outcome?: string;
}

const numberOrDash = (n: number | undefined) => (isNaN(Number(n)) ? 0 : n);

const StaticIndicatorItem: React.FC<StaticIndicatorItemProps> = ({ title, value, percentage, toolTip, link }) => (
  <span className="static-indicator-info">
    <p>
      <span className="col number">
        <CountUp end={Number(value)} duration={800} />
      </span>
      <CustomTooltip content={toolTip} direction="bottom">
        <span className="col percentage">{percentage}%</span>
      </CustomTooltip>
    </p>
    {link ? (
      <Link to={link}>
        <p className="col title text-uppercase underline">{title}</p>
      </Link>
    ) : (
      <p className="col title text-uppercase">{title}</p>
    )}
  </span>
);

const StaticIndicatorTab: React.FC<StaticIndicatorTabProps> = ({ color1, color2, icon: Icon, data, total }) => (
  <div
    className="grid-item"
    style={{ paddingTop: '18px', background: `linear-gradient(225deg, ${color1} 0%, ${color2} 100%)` }}
  >
    {/*<div className="icon-container">*/}
    {/*  {Icon && (*/}
    {/*    // eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
    {/*    //@ts-ignore*/}
    {/*    <Icon className="icon-icon" height="30px" width="30px" fill="white" />*/}
    {/*  )}*/}
    {/*</div>*/}

    <div className="row" style={{ position: 'relative', zIndex: 1 }}>
      {data.map(item => (
        <StaticIndicatorItem key={item.title} {...item} />
      ))}
    </div>

    <hr className="divider" />
    <div className="d-flex align-items-center justify-content-end">
      <h6 className="font-weight-light text-white px-3">Total No: {total}</h6>
    </div>
  </div>
);

const StaticIndicatorSubItem: React.FC<StaticIndicatorItemProps> = (props: StaticIndicatorItemProps) => {
  return (
    <span className="static-indicator-info">
      <p>
        <span className="col  number">{props.value}</span>
        <CustomTooltip content={props.toolTip} direction="bottom">
          <span id={props.toolTip} className="col  percentage">
            {props.percentage}%
          </span>
        </CustomTooltip>
      </p>
      <p className="col title text-uppercase">{props.title}</p>
    </span>
  );
};

const StaticIndicatorSubTab: React.FC<StaticIndicatorTabProps> = (props: StaticIndicatorTabProps) => {
  const Icon = props.icon;
  return (
    <div className="sub-grid-item">
      <div className="icon-container">
        <i className="icon">
          {props.icon ? (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            <Icon className="icon-icon" height="40px" width="40px" fill="#BD7AE3" />
          ) : null}
        </i>
      </div>
      <div className="row">
        {props.data?.map((item: any, index: number) => {
          return (
            <StaticIndicatorSubItem
              toolTip={item.toolTip}
              key={index}
              title={item.title}
              value={item.value}
              percentage={item.percentage}
            />
          );
        })}
      </div>
    </div>
  );
};

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

async function fetchWithRetries<T>(fn: () => Promise<T>, retries = 3): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (retries <= 0) throw err;
    await sleep((4 - retries) * 2000);
    return fetchWithRetries(fn, retries - 1);
  }
}

const StaticIndicator: React.FC<StaticIndicatorProps> = props => {
  const currentUser = useContext<Partial<UserContextProps>>(UserContext).currentUser;
  const handleError = useErrorHandler();
  const defaultValues: StaticIndicatorValues = {
    openCases: 0,
    openCasesPercentage: '0',
    closedCases: 0,
    closedCasesPercentage: '0',
    openMisconductCases: 0,
    openMisconductCasesPercentage: '0',
    openPoorPerformanceCases: 0,
    openPoorPerformanceCasesPercentage: '0',
    casesWithHearingsConducted: 0,
    casesWithHearingsConductedPercentage: '0',
    casesWithDiscussionsConducted: 0,
    casesWithDiscussionsConductedPercentage: '0',
    casesWithActivePIPs: 0,
    casesWithActivePIPsPercentage: '0',
    casesWithCounsellingProcesses: 0,
    casesWithCounsellingProcessesPercentage: '0',
    casesReferredToDisputePhase: 0,
    casesReferredToDisputePhasePercentage: '0',
    casesWithActiveSuspensions: 0,
    casesWithActiveSuspensionsPercentage: '0',
    successfulPIPCount: 0,
    successfulPIPCountPercentage: '0',
    unsuccessfulPIPCount: 0,
    unsuccessfulPIPCountPercentage: '0',
    casesWithDismissals: 0,
    casesWithDismissalsPercentage: '0',
    casesWithDemotions: 0,
    casesWithDemotionsPercentage: '0',
    casesWithPrivateSettlement: 0,
    casesWithPrivateSettlementPercentage: '0',
    casesSettledAtConciliation: 0,
    casesSettledAtConciliationPercentage: '0',
    casesLostAtArbitrationOrAdjudication: 0,
    casesLostAtArbitrationOrAdjudicationPercentage: '0',
    caseWithActiveInvestigations: 0,
    caseWithActiveInvestigationsPercentage: '0',
    casesWithIncapacityHearingsConducted: 0,
    casesWithIncapacityHearingsConductedPercentage: '0',
    casesWithActiveWarnings: 0,
    casesWithActiveWarningsPercentage: '0',
  };
  const [values, setValues] = useState<StaticIndicatorValues>(defaultValues);

  useEffect(() => {
    if (!currentUser?.organisationId) return;

    const load = async () => {
      const { rows = [] } = await fetchWithRetries(() =>
        getFlowableCasesForOrganisationCaseTable(currentUser.organisationId, 0, 10000),
      );
      const extendedRows = rows as ExtendedCaseData[];
      const cleaned = extendedRows.filter(r => r.caseNumber !== 'None');

      const pct = (num: number) => ((num / (cleaned.length || 1)) * 100).toFixed(0);
      const closedStatuses = ['Case Closed', 'Dispute Referred'];

      const newVals: StaticIndicatorValues = {
        ...defaultValues,
        openCases: cleaned.filter(r => !closedStatuses.includes(r.caseStatus)).length,
        openCasesPercentage: pct(cleaned.filter(r => !closedStatuses.includes(r.caseStatus)).length),
        closedCases: cleaned.filter(r => closedStatuses.includes(r.caseStatus)).length,
        closedCasesPercentage: pct(cleaned.filter(r => closedStatuses.includes(r.caseStatus)).length),
        openMisconductCases: cleaned.filter(r => r.caseNumber.includes('M') && !closedStatuses.includes(r.caseStatus))
          .length,
        openMisconductCasesPercentage: pct(
          cleaned.filter(r => r.caseNumber.includes('M') && !closedStatuses.includes(r.caseStatus)).length,
        ),
        openPoorPerformanceCases: cleaned.filter(
          r => r.caseNumber.includes('P') && !closedStatuses.includes(r.caseStatus),
        ).length,
        openPoorPerformanceCasesPercentage: pct(
          cleaned.filter(r => r.caseNumber.includes('P') && !closedStatuses.includes(r.caseStatus)).length,
        ),
        casesWithHearingsConducted: cleaned.filter(r => r.caseNumber.includes('M') && r.caseStatus.includes('Hearing'))
          .length,
        casesWithHearingsConductedPercentage: pct(
          cleaned.filter(r => r.caseNumber.includes('M') && r.caseStatus.includes('Hearing')).length,
        ),
        casesWithDiscussionsConducted: cleaned.filter(
          r => r.caseNumber.includes('M') && r.caseStatus.includes('Discussion'),
        ).length,
        casesWithDiscussionsConductedPercentage: pct(
          cleaned.filter(r => r.caseNumber.includes('M') && r.caseStatus.includes('Discussion')).length,
        ),
        casesWithActivePIPs: cleaned.filter(r => r.caseNumber.includes('P') && r.caseStatus.includes('PIP')).length,
        casesWithActivePIPsPercentage: pct(
          cleaned.filter(r => r.caseNumber.includes('P') && r.caseStatus.includes('PIP')).length,
        ),
        casesWithCounsellingProcesses: cleaned.filter(r => r.caseStatus.includes('counselling')).length,
        casesWithCounsellingProcessesPercentage: pct(cleaned.filter(r => r.caseStatus.includes('counselling')).length),
        casesReferredToDisputePhase: cleaned.filter(r => r.isDisputeReferred).length,
        casesReferredToDisputePhasePercentage: pct(cleaned.filter(r => r.isDisputeReferred).length),
        casesWithActiveSuspensions: cleaned.filter(r => r.isSuspensionActive).length,
        casesWithActiveSuspensionsPercentage: pct(cleaned.filter(r => r.isSuspensionActive).length),
        casesWithActiveWarnings: cleaned.filter(r => r.isWarningActive).length,
        casesWithActiveWarningsPercentage: pct(cleaned.filter(r => r.isWarningActive).length),
        caseWithActiveInvestigations: cleaned.filter(r => r.isUnderInvestigation).length,
        caseWithActiveInvestigationsPercentage: pct(cleaned.filter(r => r.isUnderInvestigation).length),
        casesLostAtArbitrationOrAdjudication: cleaned.filter(
          r => r.outcome === 'LOST' && r.caseStatus === 'Dispute Referred',
        ).length,
        casesLostAtArbitrationOrAdjudicationPercentage: pct(
          cleaned.filter(r => r.outcome === 'LOST' && r.caseStatus === 'Dispute Referred').length,
        ),
        casesSettledAtConciliation: cleaned.filter(r => r.settlementType === 'Conciliation').length,
        casesSettledAtConciliationPercentage: pct(cleaned.filter(r => r.settlementType === 'Conciliation').length),
        casesWithPrivateSettlement: cleaned.filter(r => r.settlementType === 'Private Settlement').length,
        casesWithPrivateSettlementPercentage: pct(
          cleaned.filter(r => r.settlementType === 'Private Settlement').length,
        ),
        casesWithDemotions: cleaned.filter(r => r.finalOutcome === 'Demotion').length,
        casesWithDemotionsPercentage: pct(cleaned.filter(r => r.finalOutcome === 'Demotion').length),
        casesWithDismissals: cleaned.filter(r => r.finalOutcome === 'Dismissal').length,
        casesWithDismissalsPercentage: pct(cleaned.filter(r => r.finalOutcome === 'Dismissal').length),
        casesWithIncapacityHearingsConducted: cleaned.filter(
          r => r.caseStatus.includes('Hearing') && r.caseNumber.includes('P'),
        ).length,
        casesWithIncapacityHearingsConductedPercentage: pct(
          cleaned.filter(r => r.caseStatus.includes('Hearing') && r.caseNumber.includes('P')).length,
        ),
      };

      setValues(newVals);

      if (props.setCaseData) {
        props.setCaseData(cleaned);
      }
    };

    load().catch(handleError);
  }, [currentUser?.organisationId]);

  return (
    <>
      {props.showMain && (
        <div className="static-indicator-grid">
          <StaticIndicatorTab
            color1="#50E3C2"
            color2="#4AC1FF"
            icon={GroupIcon}
            data={[
              {
                title: 'Current Cases',
                value: numberOrDash(values.openCases),
                percentage: values.openCasesPercentage,
                toolTip: 'Current open cases out of the total',
                link: 'cases?filter=open',
              },
              {
                title: 'Closed Cases',
                value: numberOrDash(values.closedCases),
                percentage: values.closedCasesPercentage,
                toolTip: 'Closed cases out of the total',
                link: 'cases?filter=closed',
              },
            ]}
            total={values.openCases + values.closedCases}
          />

          <StaticIndicatorTab
            color1="#BD7AE3"
            color2="#8461C9"
            icon={AuctionIcon}
            data={[
              {
                title: 'Current Misconduct Cases',
                value: numberOrDash(values.openMisconductCases),
                percentage: values.openMisconductCasesPercentage,
                toolTip: 'Open misconduct cases',
                link: 'cases?filter=misconduct',
              },
              {
                title: 'Current Poor Performance Cases',
                value: numberOrDash(values.openPoorPerformanceCases),
                percentage: values.openPoorPerformanceCasesPercentage,
                toolTip: 'Open poor-performance cases',
                link: 'cases?filter=performance',
              },
            ]}
            total={values.openMisconductCases + values.openPoorPerformanceCases}
          />

          <StaticIndicatorTab
            color1="#7254F2"
            color2="#55F0FC"
            icon={AuctionIcon}
            data={[
              {
                title: 'Current Disciplinary Hearings',
                value: numberOrDash(values.casesWithHearingsConducted),
                percentage: values.casesWithHearingsConductedPercentage,
                toolTip: 'Hearings in current misconduct cases',
                link: 'cases?filter=hearings',
              },
              {
                title: 'Current Disciplinary Discussions',
                value: numberOrDash(values.casesWithDiscussionsConducted),
                percentage: values.casesWithDiscussionsConductedPercentage,
                toolTip: 'Discussions in current misconduct cases',
                link: 'cases?filter=discussions',
              },
            ]}
            total={values.casesWithHearingsConducted + values.casesWithDiscussionsConducted}
          />

          <StaticIndicatorTab
            color1="#FFB97A"
            color2="#FF69A4"
            icon={ClockIcon}
            data={[
              {
                title: 'Current PIPs',
                value: numberOrDash(values.casesWithActivePIPs),
                percentage: values.casesWithActivePIPsPercentage,
                toolTip: 'Active PIPs in current poor-performance cases',
                link: 'cases?filter=activepip',
              },
              {
                title: 'Current Counselling Processes',
                value: numberOrDash(values.casesWithCounsellingProcesses),
                percentage: values.casesWithCounsellingProcessesPercentage,
                toolTip: 'Counselling in current poor-performance cases',
                link: 'cases?filter=counselling',
              },
            ]}
            total={values.casesWithActivePIPs + values.casesWithCounsellingProcesses}
          />
        </div>
      )}
      {props.showBottomMain && (
        <div className="static-indicator-grid">
          <StaticIndicatorTab
            color1={'#FFB97A'}
            color2={'#FF69A4'}
            data={[
              {
                title: 'Successful PIPs',
                percentage: values?.successfulPIPCountPercentage,
                value: values?.successfulPIPCount,
                toolTip: 'Cases with successful PIPs out of the total cases with PIPs',
              },
              {
                title: 'Unsuccessful PIPs',
                percentage: values?.unsuccessfulPIPCountPercentage,
                value: values?.unsuccessfulPIPCount,
                toolTip: 'Cases with unsuccessful PIPs out of the total cases with PIPs',
              },
            ]}
            total={values ? values?.unsuccessfulPIPCount + values?.successfulPIPCount : 0}
            icon={GroupIcon}
          />
          <StaticIndicatorTab
            color1={'#50E3C2'}
            color2={'#4AC1FF'}
            data={[
              {
                title: 'Dismissals',
                percentage: values?.casesWithDismissalsPercentage,
                value: values?.casesWithDismissals,
                toolTip: 'Dismissal outcomes from total cases',
              },
              {
                title: 'Demotions',
                percentage: values?.casesWithDemotionsPercentage,
                value: values?.casesWithDemotions,
                toolTip: 'Demotion outcomes from total cases',
              },
            ]}
            total={values ? values?.casesWithDemotions + values?.casesWithDismissals : 0}
            icon={GroupIcon}
          />
        </div>
      )}
      {props.showSub && (
        <div className="static-indicator-grid">
          <div className="sub">
            <StaticIndicatorSubTab
              color1={'#FFB97A'}
              color2={'#FF69A4'}
              data={[
                {
                  title: 'Current Suspensions',
                  percentage: values?.casesWithActiveSuspensionsPercentage,
                  value: values?.casesWithActiveSuspensions,
                  toolTip: 'Current cases with suspensions out of total open cases',
                },
              ]}
              icon={AlertCircle}
            />
            <StaticIndicatorSubTab
              color1={'#FFB97A'}
              color2={'#FF69A4'}
              data={[
                {
                  title: 'Current Warnings',
                  percentage: values?.casesWithActiveWarningsPercentage,
                  value: values?.casesWithActiveWarnings,
                  toolTip: 'Current cases with warnings out of total open cases',
                },
              ]}
              icon={AlertCircle}
            />
            <StaticIndicatorSubTab
              color1={'#FF69A4'}
              color2={'#FFB97A'}
              data={[
                {
                  title: 'Lost at Arbitration / Adjudication',
                  percentage: values?.casesLostAtArbitrationOrAdjudicationPercentage,
                  value: values?.casesLostAtArbitrationOrAdjudication,
                  toolTip: 'Current cases lost at arbitration or adjudication out of referred to dispute cases',
                },
              ]}
              icon={ToDoListIcon}
            />
            <StaticIndicatorSubTab
              color1={'#FFB97A'}
              color2={'#FF69A4'}
              data={[
                {
                  title: 'Current Investigations',
                  percentage: values?.caseWithActiveInvestigationsPercentage,
                  value: values?.caseWithActiveInvestigations,
                  toolTip: 'Current cases with active investigations out of total open cases',
                },
              ]}
              icon={ToDoListIcon}
            />
            <StaticIndicatorSubTab
              color1={'#FFB97A'}
              color2={'#FF69A4'}
              data={[
                {
                  title: 'Dispute Phase',
                  percentage: values?.casesReferredToDisputePhasePercentage,
                  value: values?.casesReferredToDisputePhase,
                  toolTip: 'Cases referred to dispute out of total open cases',
                },
              ]}
              icon={AlertCircle}
            />
            <StaticIndicatorSubTab
              color1={'#FFB97A'}
              color2={'#FF69A4'}
              data={[
                {
                  title: 'Private Settlements',
                  percentage: values?.casesWithPrivateSettlementPercentage,
                  value: values?.casesWithPrivateSettlement,
                  toolTip: 'Cases referred to private settlements out of total open cases',
                },
              ]}
              icon={SettlementIcon}
            />
            <StaticIndicatorSubTab
              color1={'#FFB97A'}
              color2={'#FF69A4'}
              data={[
                {
                  title: 'Conciliation Settlements',
                  percentage: values?.casesSettledAtConciliationPercentage,
                  value: values?.casesSettledAtConciliation,
                  toolTip: 'Cases referred to conciliation settlements out of total open cases',
                },
              ]}
              icon={SettlementIcon}
            />
            <StaticIndicatorSubTab
              color1={'#FFB97A'}
              color2={'#FF69A4'}
              data={[
                {
                  title: 'Incapacity Hearings',
                  percentage: values?.casesWithIncapacityHearingsConductedPercentage,
                  value: values?.casesWithIncapacityHearingsConducted,
                  toolTip: 'Cases with incapacity hearings out of total poor performance cases',
                },
              ]}
              icon={SettlementIcon}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default StaticIndicator;
